<!--
  -  Copyright (c) Qwoach LLC 2021. All rights reserved.
  -  Unauthorized copying of this file, via any medium is strictly prohibited
  -  Proprietary and confidential
  -  Created by Artem Bogush <artem@qwoach.com> on 10/12/2021
  -->

<template>

	<div class="cy-editor-root relative" v-bind="attrsNoListeners">
		<div ref="cyDiv">
			<div :id="containerSelectorToolbar" class="cy-toolbar" v-show="!readonly"></div>
			<div id="editor" :style="{top: readonly ? '0' : '44px'}">
				<div :id="containerSelectorLeft" class="left" :style="{'display': leftPanelDisplayComputed}"></div>
				<div :id="containerSelectorCyDiv" class="cy-main"></div>

				<div class="overflow-y" style="z-index: 1001" v-if="!readonly && hasSelected">
<!--					<slot name="detailsPanel"-->
<!--						  :clientProfile="clientProfile"-->
<!--						  :dataManagerProp="dataManager"-->
<!--						  :selectedNodes="selectedNodes"-->
<!--						  :selectedEdges="selectedEdges"-->
<!--						  :cyEditor="cyComputed">-->
						<ElementDetailsPanel
							ref="elementDetailsPanel"
							:cyEditor="cyComputed"
							:client-profile="clientProfile"
							:dataManagerProp="dataManager"
							:hideFamilyRelationships="hideFamilyRelationships"
							:width="widthComputed"
							:genogramItem="genogramItem"
							:selectedNodes="selectedNodes"
							:selectedEdges="selectedEdges">
							<template #nodeDetailsPanel="data">
								<slot name="nodeDetailsPanel" v-bind="data"></slot>
							</template>
							<template #edgeDetailsPanel="data">
								<slot name="edgeDetailsPanel" v-bind="data"></slot>
							</template>
						</ElementDetailsPanel>
<!--					</slot>-->
				</div>
			</div>
		</div>
		<div class="fixed"
			 :style="{'bottom': legendBottomComputed, 'right': '10px', 'z-index': 1000}">
			<GenogramLegendView
				ref="legendView"
				legendWidth="300px"
				:legendInitialVisibility="genogramItem.isLegendVisible"
				v-show="showLegend"
				v-if="cyComputed"
				:cyEditor="cyComputed"
				@legendVisibilityChange="onLegendVisibilityChanged"/>
		</div>
	</div>

</template>

<script>
import './assets/index.css'

import ClientProfile from "@/core/js/model/Profile/ClientProfile";

import CyEditor from "./lib";
import { defineAsyncComponent } from 'vue'
//we lazy load the ElementDetailsPanel for initial load performance since it's only needed first when the user selects an element
let ElementDetailsPanel = defineAsyncComponent(() => import("@/core/recoveryCoachingApp/coach/clients/ClientDetails/Genogram/GenogramEditor/lib/cyeditor-edit-elements/ElementDetailsPanel.vue"));
// import ElementDetailsPanel from "@/core/recoveryCoachingApp/coach/clients/ClientDetails/Genogram/GenogramEditor/lib/cyeditor-edit-elements/ElementDetailsPanel.vue";
import Debouncer from "@shared/Debouncer";
import GenogramLegendView
	from "@/core/recoveryCoachingApp/coach/clients/ClientDetails/Genogram/GenogramEditor/GenogramLegendView.vue";
import GenogramItem from "@shared/Genogram/GenogramItem";
import BaseComponent from "@/core/components/BaseComponent.vue";
import {uuidv4} from "@/utils/helpers.js";
import {isMobileScreen} from "@/core/screenHelper";
import debounce from "@/core/recoveryCoachingApp/coach/clients/ClientDetails/Genogram/GenogramEditor/utils/debounce";

export default {
	components: {GenogramLegendView, ElementDetailsPanel},
	extends: BaseComponent,

	name: 'CyEditorView',
	props: {
		clientProfile: {ClientProfile, required: true},
		genogramItem: {GenogramItem, required: true},
		cyConfig: {Object, default: () => ({})},
		editorConfig: {Object, default: () => ({})},
		readonly: Boolean,
		hideFamilyRelationships: Boolean,
	},

	inheritAttrs: false,

	data: function() {
		this.cyEditor = null //non-reactive cyEditor
		return {
			actionListenerExternal: null,
			showLegend: true,
			isReady: false,
			emitChangeDebouncer: null,
			hasSelected: false,
			selectedNodes: [],
			selectedEdges: [],
			cy: null,
			containerSelectorCyDiv: "cy-" + uuidv4(),
			containerSelectorLeft: "cy-left-" + uuidv4(),
			containerSelectorToolbar: "cy-toolbar-" + uuidv4(),
			leftPanelVisible: true,
		}
	},

	created() {

		this.onGenogramChangedDebounced = new Debouncer(this.onGenogramChanged.bind(this), 1000)
		if(isMobileScreen()){
			this.onLegendVisibilityChanged(false)
		}
	},

	mounted() {
		const container = this.$refs.cyDiv
		console.log("container = ", container)
		const defaultFn = () => {
			console.log("Genogram.default action listener")
		}
		this.actionListenerExternal = this.editorConfig.actionListener || defaultFn
		delete this.editorConfig.actionListener
		let config = {
			cy: {
				...this.cyConfig,
				autolock: this.readonly,
				readonly: this.readonly,
				container: "#" + this.containerSelectorCyDiv,
				containerSelectorLeft: "#" + this.containerSelectorLeft,
				containerSelectorToolbar: "#" + this.containerSelectorToolbar,
			},
			editor: {
				container,
				actionListener: this.actionListener,
				...this.editorConfig
			}
		}
		this.cyEditor = new CyEditor(config, this.$analyticManager)
		this.cy = this.cyEditor.cy
		console.log("cyEditor created ")

		this.showElementsInfoDebounced = debounce(this.showElementsInfo, 50)

		let defaultJsonParams = {
			userPanningEnabled: true,
			userZoomingEnabled: true,
			panningEnabled: true,
			zoomingEnabled: true
		}
		this.cyEditor.json({
			...this.genogramItem.jsonData,
			...defaultJsonParams
		})

		this.cyEditor.on('change', () => {
			this.onGenogramChangedDebounced.onChangeDebounced()
		})

		this.cyEditor.on('requestLegendSnapshot', async () => {
			return await this.makeLegendSnapshot()
		})
		this.isReady = true
		this.onCyReady()
	},

	methods: {

		async actionListener(command, params) {
			let res = await this.actionListenerExternal(command, params)
			console.log("CyEditorView.actionListener: ", command, res)
			if(res !== undefined){
				return res
			}

			switch (command.action) {
				case "toggleLeftPanel":
					this.leftPanelVisible = !this.leftPanelVisible
					return true
			}

			return undefined //indicates that the action was not handled
		},

		onCyReady() {
			console.log("onCyReady: ", this.cy)
			this.cy.on('select unselect remove', (e) => {
				console.log("showElementsInfo: e = ", e)
				this.showElementsInfoDebounced() //we need to debounce this to ensure that the selection is correct because the selection events are fired multiple times
			})
			this.cy.on('tap', (e) => {
				console.log("showElementsInfo2: e = ", e)
				if(this.$refs.elementDetailsPanel){
					this.$refs.elementDetailsPanel.resetVisibility({force: true})
				}
			})
			this.syncLabels()
		},

		syncLabels(){
			let nodes = this.cy.nodes()
			nodes.forEach(node => {
				let data = node.data()
				this.genogramItem.updateNodeLabel(data)
				node.data(data)
			})
		},

		async showElementsInfo() {
			console.log("showElementsInfo: start")
			let selected = this.cy.$(':selected')
			let hasSelected = selected.length === 1 && !selected[0].data().isAuxNode

			if(this.hasSelected !== hasSelected){
				await this.emitPromise("detailsVisibilityChanged", hasSelected)
			}
			this.hasSelected = hasSelected
			this.showLegend = !this.hasSelected
			console.log("showElementsInfo: selected = ", selected)
			this.selectedNodes = selected.map(elem => elem.isNode() ? elem.data() : null).filter(e => e != null)
			this.selectedEdges = selected.map(elem => elem.isEdge() ? elem.data() : null).filter(e => e != null)
			if(this.$refs.elementDetailsPanel){
				this.$refs.elementDetailsPanel.resetVisibility()
			}
			console.log("showElementsInfo: end")
		},

		async makeLegendSnapshot(){
			this.showLegend = true
			let img = await this.$refs.legendView.requestSnapshot()
			return img
		},

		onElementDetailsPanelVisibilityChange(isPanelVisible) {
			this.showLegend = !isPanelVisible
		},

		onLegendVisibilityChanged(isVisible) {
			this.genogramItem.isLegendVisible = isVisible
			this.$emit("change", this.genogramItem)
		},

		onGenogramChanged() {
			let json = this.cyEditor.json()
			if (json.elements.nodes) {
				json.elements.nodes = json.elements.nodes.filter(node => !node.data.tempElement)
			}
			console.log("genogram changed: ", json)
			this.genogramItem.jsonData = json
			this.$emit("change", this.genogramItem)
		},
	},
	computed: {
		cyComputed() {
			return this.isReady && this.cyEditor ? this.cyEditor : null
		},
		widthComputed(){
			let width
			if(this.editorConfig.rightPanel){
				width = this.editorConfig.rightPanel.width
			}
			return width || "300px"
		},

		leftPanelDisplayComputed(){
			return this.leftPanelVisible ? "block" : "none"
		},

		legendBottomComputed(){
			return this.cyConfig.legendBottom || "10px"
		}
	},


}
</script>

<style lang="scss">

.cy-editor-root{

	.symbols-tooltip{
		background: darkgray;
	}
	.symbols-tooltip::after {
		opacity: 0;
	}
	.symbols-tooltip {
		&:focus, &:hover, &:active {
			&::after {
				content: attr(data-tooltip);
				opacity: 1;

				animation-name: show;
				animation-duration: 0.5s;
				font-size: 12px;
				width: auto;
				height: auto;
				max-width: 200px;
				white-space: nowrap;
				background-color: #555;
				color: #fff;
				text-align: center;
				border-radius: 3px;
				padding: 4px 8px;
				position: absolute;
				z-index: 1000;
				//bottom: 125%;
				//left: 50%;

				top: 30px;
			}
		}

	}

	//.symbols-tooltip:focus::after, .symbols-tooltip:hover::after {
	//
	//}

	.cy-toolbar{
		[class^="icon-"]:before, [class*=" icon-"]:before {
			//font-family: inherit !important;
			margin-left: 0 !important;
			//margin-right: 0 !important;
		}
		.icon-to-png{
			font-family: "fontello" !important;
		}
		.icon-to-png:before {
			content: "\e82a";
		}

		i{
			position: relative;
		}
		i::after{
			opacity: 0;
		}


		i:hover:not(.touch-device)::after{
			animation-name: show;
			animation-duration: 0.5s;
			opacity: 1;
			font-size: 12px;
			content: attr(data-title);
			width: auto;
			white-space: nowrap;
			background-color: #555;
			color: #fff;
			text-align: center;
			border-radius: 3px;
			padding: 4px 8px;
			position: absolute;
			z-index: 1000;
			//bottom: 125%;
			left: 50%;

			top: 30px;
		}

		@keyframes show {
			0% {
				opacity: 0;
			}

			99% {
				opacity: 0;
			}

			100% {
				opacity: 1;
			}
		}
	}
}


</style>
